import React, { useState, useEffect } from 'react';
import { Button, Card, Form, Input, InputNumber, Radio, Space, Spin, Typography, Upload, message } from 'antd';
import { UploadOutlined, FileTextOutlined, BuildOutlined, CopyOutlined } from '@ant-design/icons';
import { 
  AB1FileResult, 
  FastaSequence, 
  AssemblyPath, 
  Contig, 
  AssemblyResult,
  assembleAB1Sequences,
  assembleFastaSequences
} from '@/api/sequencingService';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

interface SequenceAssemblyProps {
  ab1Results?: AB1FileResult[]; // 可选的AB1解析结果
}

const SequenceAssembly: React.FC<SequenceAssemblyProps> = ({ ab1Results }) => {
  const [inputType, setInputType] = useState<'ab1' | 'fasta'>(ab1Results && ab1Results.length > 1 ? 'ab1' : 'fasta');
  const [fastaInput, setFastaInput] = useState<string>('');
  const [minOverlapLength, setMinOverlapLength] = useState<number>(15);
  const [minPhredScore, setMinPhredScore] = useState<number>(10);
  const [loading, setLoading] = useState<boolean>(false);
  const [assemblyResult, setAssemblyResult] = useState<AssemblyResult | null>(null);

  // 当ab1Results变化时更新输入类型
  useEffect(() => {
    if (ab1Results && ab1Results.length > 1) {
      setInputType('ab1');
    } else {
      setInputType('fasta');
    }
  }, [ab1Results]);

  // 解析FASTA格式的输入
  const parseFastaInput = (input: string): FastaSequence[] => {
    const sequences: FastaSequence[] = [];
    const fastaEntries = input.split('>').filter(entry => entry.trim().length > 0);
    
    fastaEntries.forEach(entry => {
      const lines = entry.split('\n');
      const header = lines[0].trim();
      const sequence = lines.slice(1).join('').replace(/\s/g, '');
      
      if (header && sequence) {
        sequences.push({
          name: header,
          sequence: sequence
        });
      }
    });
    
    return sequences;
  };

  // 上传FASTA文件
  const handleFastaFileUpload = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        setFastaInput(content);
        const sequences = parseFastaInput(content);
        if (sequences.length > 0) {
          message.success(`成功导入 ${sequences.length} 个序列`);
        } else {
          message.error('未能从文件中解析出有效序列');
        }
      } catch (error) {
        message.error('解析FASTA文件失败');
        console.error('解析FASTA文件失败:', error);
      }
    };
    reader.readAsText(file);
    return false;  // 阻止默认上传行为
  };

  // 执行序列组装
  const handleAssembly = async () => {
    try {
      setLoading(true);
      
      if (inputType === 'ab1' && ab1Results && ab1Results.length > 1) {
        console.log('开始组装AB1序列:', ab1Results.length, '个文件');
        
        // 检查AB1结果是否有效
        if (!Array.isArray(ab1Results)) {
          throw new Error('AB1结果不是数组');
        }
        
        // 检查每个AB1结果是否有效
        const validResults = ab1Results.filter(r => r && r.data && r.data.sequence);
        if (validResults.length < 2) {
          message.error(`有效序列数量不足: ${validResults.length}/2`);
          setLoading(false);
          return;
        }
        
        // 使用AB1结果进行组装
        try {
          const result = await assembleAB1Sequences(
            validResults,
            minOverlapLength,
            minPhredScore
          );
          
          if (!result || !result.contigs) {
            throw new Error('组装结果无效');
          }
          
          setAssemblyResult(result);
          message.success(`序列组装成功，生成 ${result.contigs.length} 个contigs`);
        } catch (error) {
          console.error('AB1序列组装失败:', error);
          message.error(`AB1序列组装失败: ${error instanceof Error ? error.message : '未知错误'}`);
        }
      } else {
        // 使用FASTA序列进行组装
        // 解析FASTA输入
        const validSequences = parseFastaInput(fastaInput);
        
        if (validSequences.length < 2) {
          message.error('需要至少两个有效序列才能进行组装');
          setLoading(false);
          return;
        }
        
        console.log('开始组装FASTA序列:', validSequences.length, '个序列');
        
        try {
          const result = await assembleFastaSequences(
            validSequences,
            minOverlapLength
          );
          
          if (!result || !result.contigs) {
            throw new Error('组装结果无效');
          }
          
          setAssemblyResult(result);
          message.success(`序列组装成功，生成 ${result.contigs.length} 个contigs`);
        } catch (error) {
          console.error('FASTA序列组装失败:', error);
          message.error(`FASTA序列组装失败: ${error instanceof Error ? error.message : '未知错误'}`);
        }
      }
    } catch (error) {
      console.error('组装失败:', error);
      message.error(`组装失败: ${error instanceof Error ? error.message : '未知错误'}`);
    } finally {
      setLoading(false);
    }
  };

  // 渲染组装路径
  const renderAssemblyPath = (path: AssemblyPath[]) => {
    return (
      <div style={{ marginTop: 10, overflowX: 'auto' }}>
        <div style={{ whiteSpace: 'nowrap', padding: '5px 0' }}>
          {path.map((step, index) => (
            <span key={index}>
              {index > 0 && <span style={{ margin: '0 5px' }}>{step.direction}</span>}
              <span style={{ 
                padding: '3px 8px', 
                border: '1px solid #d9d9d9', 
                borderRadius: '4px',
                backgroundColor: '#f5f5f5'
              }}>
                {step.sequence_id}
              </span>
            </span>
          ))}
        </div>
      </div>
    );
  };

  // FASTA格式示例
  const fastaExample = `>Sequence1
ATCGATCGATCGATCG
>Sequence2
GATCGATCGATCGATC
>Sequence3
CGATCGATCGATCGAT`;

  // 添加清除函数
  const handleClear = () => {
    // 重置状态
    setFastaInput('');
    setMinOverlapLength(15);
    setMinPhredScore(10);
    setAssemblyResult(null);
    // 根据ab1Results重置输入类型
    setInputType(ab1Results && ab1Results.length > 1 ? 'ab1' : 'fasta');
    
    message.success('已清除所有内容');
  };

  // 复制拼接结果
  const copyAssemblyResult = () => {
    if (!assemblyResult || !assemblyResult.contigs) {
      message.error('没有可复制的拼接结果');
      return;
    }
    
    // 构建拼接结果文本
    let resultText = `序列组装结果 (${new Date().toLocaleString()})\n\n`;
    resultText += `共生成 ${assemblyResult.contigs.length} 个Contigs\n\n`;
    
    assemblyResult.contigs.forEach((contig, index) => {
      resultText += `=== Contig ${index + 1}: ${contig.id} ===\n`;
      resultText += `长度: ${contig.length} bp\n`;
      
      // 组装路径
      resultText += `组装路径: `;
      contig.assembly_path.forEach((step, stepIndex) => {
        if (stepIndex > 0) {
          resultText += ` ${step.direction} `;
        }
        resultText += step.sequence_id;
      });
      resultText += '\n';
      
      // 序列
      resultText += `序列:\n${contig.sequence}\n\n`;
    });
    
    // 使用兼容性更好的复制方法
    try {
      // 创建临时文本区域
      const textArea = document.createElement('textarea');
      textArea.value = resultText;
      
      // 确保文本区域不可见
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      
      // 选择并复制文本
      textArea.focus();
      textArea.select();
      const successful = document.execCommand('copy');
      
      // 移除临时元素
      document.body.removeChild(textArea);
      
      if (successful) {
        message.success('拼接结果已复制到剪贴板');
      } else {
        message.error('复制失败，请手动选择并复制');
      }
    } catch (err) {
      console.error('复制失败:', err);
      message.error('复制失败，请手动选择并复制');
    }
  };

  return (
    <div>
      <Spin spinning={loading}>
        <Form layout="vertical">
          {/* 输入类型选择 */}
          <Form.Item label="输入类型">
            <Radio.Group 
              value={inputType} 
              onChange={e => setInputType(e.target.value)}
              disabled={loading}
            >
              <Radio.Button value="ab1" disabled={!ab1Results || ab1Results.length < 2}>
                使用已解析的AB1文件
              </Radio.Button>
              <Radio.Button value="fasta">
                输入FASTA序列
              </Radio.Button>
            </Radio.Group>
          </Form.Item>
          
          {/* AB1文件信息 */}
          {inputType === 'ab1' && ab1Results && ab1Results.length > 0 && (
            <Form.Item label="AB1文件">
              <Text>已选择 {ab1Results.length} 个AB1文件用于组装</Text>
              <ul>
                {ab1Results.map((result, index) => (
                  <li key={index}>{result.filename} - 序列长度: {result.data.sequence.length} bp</li>
                ))}
              </ul>
            </Form.Item>
          )}
          
          {/* FASTA序列输入 */}
          {inputType === 'fasta' && (
            <>
              <Form.Item label="FASTA序列">
                <Space direction="vertical" style={{ width: '100%' }}>
                  <TextArea
                    placeholder={`请输入FASTA格式序列，例如：\n${fastaExample}`}
                    value={fastaInput}
                    onChange={e => setFastaInput(e.target.value)}
                    rows={10}
                    disabled={loading}
                    style={{ fontFamily: 'monospace' }}
                  />
                  <Space>
                    <Upload 
                      beforeUpload={handleFastaFileUpload}
                      showUploadList={false}
                      accept=".fasta,.fa,.seq,.txt"
                      disabled={loading}
                    >
                      <Button icon={<UploadOutlined />} disabled={loading}>
                        导入FASTA文件
                      </Button>
                    </Upload>
                    <Button 
                      onClick={() => setFastaInput('')}
                      disabled={!fastaInput || loading}
                    >
                      清空
                    </Button>
                  </Space>
                </Space>
              </Form.Item>
            </>
          )}
          
          {/* 组装参数 */}
          <Form.Item label="组装参数">
            <Space>
              <InputNumber
                addonBefore="最小重叠长度"
                min={5}
                max={100}
                value={minOverlapLength}
                onChange={value => setMinOverlapLength(value as number)}
                disabled={loading}
              />
              
              {inputType === 'ab1' && (
                <InputNumber
                  addonBefore="最小质量分数"
                  min={5}
                  max={60}
                  value={minPhredScore}
                  onChange={value => setMinPhredScore(value as number)}
                  disabled={loading}
                />
              )}
            </Space>
          </Form.Item>
          
          {/* 组装按钮 */}
          <Form.Item>
            <Space>
              <Button 
                type="primary" 
                onClick={handleAssembly}
                loading={loading}
                disabled={(inputType === 'ab1' && (!ab1Results || ab1Results.length < 2)) || 
                        (inputType === 'fasta' && parseFastaInput(fastaInput).length < 2)}
              >
                开始组装
              </Button>
              <Button 
                onClick={handleClear}
                disabled={loading}
              >
                清除
              </Button>
            </Space>
          </Form.Item>
        </Form>

        {/* 组装结果 */}
        {assemblyResult && assemblyResult.contigs && (
          <Card 
            title={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span><FileTextOutlined /> 组装结果</span>
                <Button 
                  icon={<CopyOutlined />} 
                  onClick={copyAssemblyResult}
                  type="link"
                >
                  复制全部结果
                </Button>
              </div>
            } 
            style={{ marginTop: 20 }}
          >
            <Title level={4}>共生成 {assemblyResult.contigs.length} 个Contigs</Title>
            
            {assemblyResult.contigs.map((contig, index) => (
              <Card key={index} type="inner" title={`Contig ${index + 1}: ${contig.id}`} style={{ marginBottom: 16 }}>
                <Paragraph>
                  <Text strong>长度:</Text> {contig.length} bp
                </Paragraph>
                
                <Paragraph>
                  <Text strong>组装路径:</Text>
                  {renderAssemblyPath(contig.assembly_path)}
                </Paragraph>
                
                <Paragraph>
                  <Text strong>序列:</Text>
                  <div style={{ 
                    marginTop: 8,
                    padding: 10,
                    backgroundColor: '#f5f5f5',
                    borderRadius: 4,
                    fontFamily: 'monospace',
                    fontSize: '14px',
                    overflowX: 'auto',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all'
                  }}>
                    {contig.sequence}
                  </div>
                </Paragraph>
              </Card>
            ))}
          </Card>
        )}
      </Spin>
    </div>
  );
};

export default SequenceAssembly;